import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "3 de noviembre de 2026",
  lastReviewed: "3 de noviembre de 2026",
  lastUpdated: "3 de noviembre de 2026",
};

const cole2020 = {
  citation:
    "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
  url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
};
const cole2022 = {
  citation:
    "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
  url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
};

// Respuestas breves y autocontenidas, en espa\u00f1ol natural, una bajo cada H2.
const r1 =
  "SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation Therapy) es un tratamiento autorizado por la FDA para adultos con trastorno depresivo mayor que no han mejorado con los antidepresivos. Usa una resonancia magn\u00e9tica funcional (fMRI) para encontrar un objetivo de tratamiento personalizado y administra 50 sesiones cortas de estimulaci\u00f3n magn\u00e9tica durante cinco d\u00edas, en lugar de las seis a nueve semanas de la TMS convencional.";
const r2 =
  "SAINT significa Stanford Accelerated Intelligent Neuromodulation Therapy. Investigadores de la Universidad de Stanford lo desarrollaron, y en su ensayo de 2022 lo llamaron Stanford Neuromodulation Therapy (SNT); es el mismo tratamiento. Magnus Medical fabrica el sistema SAINT\u00ae autorizado por la FDA, y solo los centros listados en el localizador oficial de Magnus Medical lo ofrecen.";
const r3 =
  "SAINT\u00ae comienza con una resonancia magn\u00e9tica funcional de unos 45 minutos que mapea c\u00f3mo se comunican las \u00e1reas del cerebro. Un software elige el punto de la corteza prefrontal a estimular. El tratamiento consiste en diez sesiones de 10 minutos al d\u00eda, separadas por descansos de 50 minutos, durante cinco d\u00edas consecutivos: 50 sesiones en total.";
const r4 =
  "La TMS convencional se dirige a una ubicaci\u00f3n est\u00e1ndar del cuero cabelludo y suele requerir una sesi\u00f3n al d\u00eda, cinco d\u00edas por semana, durante seis a nueve semanas. SAINT\u00ae se dirige a un objetivo elegido a partir de la fMRI de cada persona y se completa en cinco d\u00edas. SAINT\u00ae es el \u00fanico TMS autorizado por la FDA que personaliza el objetivo de esta manera.";
const r5 =
  "S\u00ed. SAINT\u00ae est\u00e1 autorizado por la FDA para adultos con trastorno depresivo mayor que no mejoraron lo suficiente con medicaci\u00f3n antidepresiva previa en el episodio actual, que es la forma habitual de describir la depresi\u00f3n resistente al tratamiento. Un cl\u00ednico confirma la elegibilidad, incluyendo verificaciones de seguridad como implantes met\u00e1licos y antecedentes de convulsiones.";
const r6 =
  "En un ensayo aleatorizado de doble ciego de 2022, el 78,6% de las personas que recibieron SAINT\u00ae alcanzaron la remisi\u00f3n dentro de unas cuatro semanas despu\u00e9s del tratamiento (Cole et al., American Journal of Psychiatry, 2022). Un estudio previo en abierto inform\u00f3 aproximadamente un 90% de remisi\u00f3n en 21 participantes (Cole et al., 2020). Los resultados var\u00edan de una persona a otra.";
const r7 =
  "Los efectos secundarios m\u00e1s comunes en los ensayos fueron cefalea, fatiga y molestias en el cuero cabelludo o el sitio de tratamiento, generalmente leves y pasajeros. No se necesita anestesia ni sedaci\u00f3n. Como en toda TMS, existe un riesgo raro de convulsi\u00f3n. Las personas con metal no extra\u00edble en o cerca de la cabeza no son candidatas.";
const r8 =
  "Solo los centros listados en el localizador oficial de Magnus Medical ofrecen SAINT\u00ae. Algunos centros venden programas m\u00e1s econ\u00f3micos de \u201cTMS acelerada guiada por fMRI\u201d que copian el calendario de 5 d\u00edas pero usan su propia orientaci\u00f3nn, no autorizada, fuera de indicaci\u00f3n, por lo que no pueden reclamar los resultados ni la autorizaci\u00f3n de los ensayos de SAINT. Neuro Wellness es el proveedor verificado de SAINT\u00ae en Florida.";
const r9 =
  "SAINT\u00ae en Neuro Wellness parte de $25,000 por el curso completo de cinco d\u00edas, incluyendo la fMRI y las 50 sesiones. Medicare tiene una tarifa de pago para SAINT\u00ae. La cobertura del seguro comercial es limitada y se decide caso por caso mediante autorizaci\u00f3n previa. Verificamos sus beneficios sin costo.";

const faqs: LearnFAQ[] = [
  { question: "\u00bfQu\u00e9 es SAINT\u00ae TMS?", answer: r1 },
  {
    question: "\u00bfQu\u00e9 significa SAINT y es lo mismo que SNT?",
    answer: r2,
  },
  {
    question: "\u00bfC\u00f3mo funciona el protocolo SAINT\u00ae?",
    answer: r3,
  },
  {
    question:
      "\u00bfEn qu\u00e9 se diferencia SAINT\u00ae de la TMS convencional?",
    answer: r4,
  },
  {
    question:
      "\u00bfSAINT\u00ae es para la depresi\u00f3n resistente al tratamiento?",
    answer: r5,
  },
  {
    question: "\u00bfSAINT\u00ae funciona y qu\u00e9 mostraron los ensayos?",
    answer: r6,
  },
  {
    question: "\u00bfCu\u00e1les son los efectos secundarios de SAINT\u00ae?",
    answer: r7,
  },
  {
    question:
      "\u00bfC\u00f3mo s\u00e9 si un centro ofrece SAINT\u00ae verdadero?",
    answer: r8,
  },
  {
    question: "\u00bfCu\u00e1nto cuesta SAINT\u00ae y lo cubre el seguro?",
    answer: r9,
  },
];

export const esWhatIsSaintTms = makeEsArticle({
  slug: "what-is-saint-tms",
  title:
    "\u00bfQu\u00e9 es SAINT\u00ae TMS? El tratamiento de 5 d\u00edas desarrollado en Stanford",
  description:
    "SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation Therapy, tambi\u00e9n llamado SNT) explicado: c\u00f3mo funciona la TMS guiada por fMRI de 5 d\u00edas, resultados, seguridad, costo.",
  h1: "\u00bfQu\u00e9 es SAINT\u00ae TMS? El tratamiento de 5 d\u00edas desarrollado en Stanford",
  breadcrumbLabel: "\u00bfQu\u00e9 es SAINT\u00ae TMS?",
  shortAnswer:
    "SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation Therapy) es un tratamiento autorizado por la FDA para adultos con depresi\u00f3n mayor que no han mejorado con antidepresivos: usa una fMRI para encontrar un objetivo personalizado y administra 50 sesiones cortas durante cinco d\u00edas, en lugar de las seis a nueve semanas de la TMS convencional. Neuro Wellness TMS Centers of America en Coral Springs es el proveedor verificado de SAINT\u00ae en Florida.",
  keyTakeaways: [
    "SAINT\u00ae est\u00e1 autorizado por la FDA (510(k) K220177, septiembre de 2022) para adultos con trastorno depresivo mayor que no mejoraron lo suficiente con antidepresivos.",
    "Administra 10 sesiones al d\u00eda durante 5 d\u00edas consecutivos (50 en total), frente a unas 30\u201336 sesiones en 4\u20136 semanas de la TMS convencional.",
    "El objetivo de estimulaci\u00f3n se elige a partir de su propia fMRI: SAINT\u00ae es el \u00fanico TMS autorizado por la FDA que personaliza el objetivo as\u00ed.",
    "En un ensayo controlado con simulaci\u00f3n de 2022, el 78,6% del grupo activo alcanz\u00f3 la remisi\u00f3n a las 4 semanas frente al 13,3% del grupo simulaci\u00f3n. Los resultados individuales var\u00edan.",
    "Neuro Wellness en Coral Springs es el proveedor verificado de SAINT\u00ae en Florida. Una consulta gratuita confirma si es adecuado para usted.",
  ],
  sections: [
    {
      heading: "\u00bfQu\u00e9 es SAINT\u00ae TMS?",
      paragraphs: [
        r1,
        "SAINT\u00ae es una forma acelerada de estimulaci\u00f3n magn\u00e9tica transcraneal (TMS) para el trastorno depresivo mayor, desarrollada en el Laboratorio de Estimulaci\u00f3n Cerebral de Stanford y administrada comercialmente en el sistema Magnus Neuromodulation System con tecnolog\u00eda SAINT, fabricado por Magnus Medical, Inc.",
        "Lo que distingue a SAINT\u00ae es su compresi\u00f3n: la TMS convencional toma una sesi\u00f3n al d\u00eda durante cuatro a seis semanas; SAINT\u00ae administra diez sesiones cortas al d\u00eda durante cinco d\u00edas consecutivos \u201450 sesiones en una semana\u2014 con un patr\u00f3n de estimulaci\u00f3n theta burst intermitente y descansos de unos 50 minutos entre sesiones.",
      ],
    },
    {
      heading: "\u00bfQu\u00e9 significa SAINT y es lo mismo que SNT?",
      paragraphs: [
        r2,
        "El ensayo aleatorizado de 2022 publicado en American Journal of Psychiatry se refiri\u00f3 al tratamiento como Stanford Neuromodulation Therapy (SNT). SAINT\u00ae y SNT describen el mismo protocolo. El sistema comercial autorizado es el Magnus Neuromodulation System con tecnolog\u00eda SAINT (510(k) K220177), disponible solo a trav\u00e9s de centros acreditados por Magnus Medical.",
      ],
    },
    {
      heading: "\u00bfC\u00f3mo funciona el protocolo SAINT\u00ae?",
      paragraphs: [
        r3,
        "Antes de la semana de tratamiento, se realiza una evaluaci\u00f3n psiqui\u00e1trica y una fMRI en reposo que mapea c\u00f3mo se comunica la corteza prefrontal con circuitos m\u00e1s profundos del estado de \u00e1nimo; un software calcula el objetivo individual de estimulaci\u00f3n en la corteza prefrontal dorsolateral izquierda.",
        "Durante la semana, la neuronavegaci\u00f3n mantiene la bobina sobre ese objetivo exacto en cada sesi\u00f3n. Cada sesi\u00f3n dura unos diez minutos; con los descansos, pasar\u00e1 la mayor parte de cada uno de los cinco d\u00edas en la cl\u00ednica. No se usa anestesia ni sedaci\u00f3n; usted permanece despierto.",
      ],
    },
    {
      heading:
        "\u00bfEn qu\u00e9 se diferencia SAINT\u00ae de la TMS convencional?",
      paragraphs: [
        r4,
        "Comparaci\u00f3n completa: [[SAINT\u00ae frente a TMS convencional|/es/learn/saint-vs-traditional-tms]].",
      ],
      table: {
        headers: ["", "SAINT\u00ae", "TMS convencional"],
        rows: [
          ["Calendario", "5 d\u00edas", "6\u20139 semanas"],
          ["Visitas a la cl\u00ednica", "5", "30\u201336"],
          [
            "Elecci\u00f3n del objetivo",
            "fMRI personalizada",
            "medici\u00f3n est\u00e1ndar del cuero cabelludo",
          ],
          ["Autorizaci\u00f3n FDA", "2022 (K220177)", "desde 2008"],
          [
            "Tasa de remisi\u00f3n publicada",
            "78,6% en ensayo de 2022",
            "var\u00eda seg\u00fan el estudio",
          ],
          [
            "Costo typical de pago directo",
            "desde $25,000",
            "consulte a su proveedor",
          ],
        ],
      },
    },
    {
      heading:
        "\u00bfSAINT\u00ae es para la depresi\u00f3n resistente al tratamiento?",
      paragraphs: [
        r5,
        "La depresi\u00f3n resistente al tratamiento suele definirse como un trastorno depresivo mayor que no ha respondido adecuadamente a al menos dos ensayos de antidepresivos con dosis y duraci\u00f3n adecuadas. La indicaci\u00f3n autorizada de SAINT\u00ae cubre a adultos en esta situaci\u00f3n; la decisi\u00f3n final la toma un psiquiatra tras una evaluaci\u00f3n cl\u00ednica.",
      ],
    },
    {
      heading: "\u00bfSAINT\u00ae funciona y qu\u00e9 mostraron los ensayos?",
      paragraphs: [
        r6,
        "En el estudio en abierto de 2020, 19 de 21 participantes (90,5%) alcanzaron la remisi\u00f3n tras cinco d\u00edas, pero no hubo comparaci\u00f3n con simulaci\u00f3n. En el ensayo aleatorizado de doble ciego de 2022, el 78,6% del grupo activo (11 de 14) alcanz\u00f3 la remisi\u00f3n a las cuatro semanas frente al 13,3% (2 de 15) del grupo simulaci\u00f3n.",
        "Ambos estudios fueron peque\u00f1os, en un \u00fanico centro y con un seguimiento corto (cuatro semanas en el ensayo aleatorizado). Los resultados deben leerse como evidencia temprana prometedora, no definitiva. Ning\u00fan ensayo ha comparado SAINT\u00ae directamente con un curso convencional de seis semanas de TMS. Los resultados individuales var\u00edan.",
      ],
    },
    {
      heading: "\u00bfCu\u00e1les son los efectos secundarios de SAINT\u00ae?",
      paragraphs: [
        r7,
        "Los m\u00e1s comunes son cefalea, molestias en el cuero cabelludo y twitching facial, que suelen disminuir en el primer o segundo d\u00eda. El riesgo m\u00e1s grave es la convulsi\u00f3n, que es raro. Las personas con metal ferromagn\u00e9tico en o cerca de la cabeza, estimuladores electr\u00f3nicos implantados o que no pueden someterse a una resonancia no son candidatas; los antecedentes de convulsiones requieren evaluaci\u00f3n. SAINT\u00ae no est\u00e1 autorizado para depresi\u00f3n bipolar, TEPT, ansiedad aislada, TOC ni menores de 18 a\u00f1os.",
      ],
    },
    {
      heading:
        "\u00bfC\u00f3mo s\u00e9 si un centro ofrece SAINT\u00ae verdadero?",
      paragraphs: [
        r8,
        "Neuro Wellness TMS Centers of America aparece en el localizador oficial de Magnus Medical. M\u00e1s informaci\u00f3n: [[c\u00f3mo verificar un proveedor de SAINT\u00ae|/es/learn/what-is-a-verified-saint-provider]] y nuestro [[aviso de SAINT\u00ae|/es/learn/saint-disclaimer]].",
      ],
    },
    {
      heading: "\u00bfCu\u00e1nto cuesta SAINT\u00ae y lo cubre el seguro?",
      paragraphs: [
        r9,
        "El precio inicial de $25,000 cubre la fMRI y las 50 sesiones del curso completo de cinco d\u00edas. Medicare tiene una tarifa de pago para SAINT\u00ae. La cobertura comercial es limitada y se decide caso por caso mediante autorizaci\u00f3n previa; por eso verificamos sus beneficios sin costo. Ver [[costo y seguro de SAINT\u00ae|/es/learn/saint-tms-cost]].",
      ],
    },
  ],
  faqs,
  sources: [cole2020, cole2022],
  about: {
    "@type": "MedicalTherapy",
    name: "SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation Therapy)",
    alternateName: ["SAINT TMS", "Stanford Neuromodulation Therapy (SNT)"],
  },
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
